import Vue from "vue";

class GoogleMapsService {
  constructor() {
    this.apiKey = process.env.GOOGLE_MAPS_API_KEY || "";
    this.loaded = false;
    this.loading = false;
    this.googlePromise = null;
  }

  get google() {
    if (!this.googlePromise) {
      this.googlePromise = this.loadGoogleMaps();
    }
    return this.googlePromise;
  }

  loadGoogleMaps() {
    if (this.loaded) return Promise.resolve(window.google);
    if (this.loading) return this.googlePromise;

    this.loading = true;

    return new Promise((resolve, reject) => {
      try {
        // Only load in client-side context
        if (typeof window === "undefined") {
          this.loading = false;
          return resolve(null);
        }

        window.initGoogleMaps = () => {
          this.loading = false;
          this.loaded = true;
          resolve(window.google);
          delete window.initGoogleMaps;
        };

        // If script is already in the DOM, don't add it again
        if (
          document.querySelector(
            'script[src*="maps.googleapis.com/maps/api/js"]'
          )
        ) {
          if (window.google) {
            this.loading = false;
            this.loaded = true;
            return resolve(window.google);
          }
          // Script exists but Google isn't loaded yet, wait for original callback
          return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      } catch (error) {
        this.loading = false;
        reject(error);
      }
    });
  }

  createMap(element, options = {}) {
    return this.google.then((google) => {
      return new google.maps.Map(element, options);
    });
  }

  createMarker(options = {}) {
    return this.google.then((google) => {
      return new google.maps.Marker(options);
    });
  }

  createInfoWindow(options = {}) {
    return this.google.then((google) => {
      return new google.maps.InfoWindow(options);
    });
  }

  createAutocomplete(element, options = {}) {
    return this.google.then((google) => {
      return new google.maps.places.Autocomplete(element, options);
    });
  }

  createGeocoder() {
    return this.google.then((google) => {
      return new google.maps.Geocoder();
    });
  }

  createLatLng(lat, lng) {
    return this.google.then((google) => {
      return new google.maps.LatLng(lat, lng);
    });
  }

  createBounds() {
    return this.google.then((google) => {
      return new google.maps.LatLngBounds();
    });
  }
}

// Create a global service instance
const googleMapsService = new GoogleMapsService();

export default (context, inject) => {
  // Inject the service as $googleMaps
  inject("googleMaps", googleMapsService);
};

// Create a component to load the Google Maps API
Vue.component("GMap", {
  render: (h) => h("div", { style: "display: none" }),
  mounted() {
    // Load Google Maps when component is mounted
    this.$googleMaps.google;
  },
});
